// Admin Stripe-sync API — typed wrappers around /admin/v1/stripe/*.

import type { ApiClient } from '../api-client.js';

export type WebhookStatus =
  | 'received'
  | 'processed'
  | 'rejected'
  | 'skipped';
export type WebhookStatusFilter = 'all' | WebhookStatus | 'stuck';

export interface WebhookRow {
  source: string;
  eventId: string;
  eventType: string;
  status: WebhookStatus;
  tenantId: string | null;
  tenantName: string | null;
  error: string | null;
  receivedAt: string;
  processedAt: string | null;
  stuck: boolean;
}

export interface MeterPusherState {
  unflushedCount: number;
  oldestUnflushedTs: string | null;
  lastFlushedAt: string | null;
  pusherWired: boolean;
}

export interface DivergenceRow {
  id: string;
  tenantId: string;
  tenantName: string;
  localStatus: string;
  stripeStatus: string;
  detectedAt: string;
  resolvedAt: string | null;
  meta: Record<string, unknown>;
}

export interface MissingSubRow {
  tenantId: string;
  tenantName: string;
  missing: 'customer' | 'subscription';
  createdAt: string;
}

export const STRIPE_WEBHOOKS_QUERY_KEY = ['admin', 'stripe', 'webhooks'] as const;
export const STRIPE_METER_PUSHER_QUERY_KEY = ['admin', 'stripe', 'meter-pusher'] as const;
export const STRIPE_DIVERGENCE_QUERY_KEY = ['admin', 'stripe', 'divergence'] as const;
export const STRIPE_MISSING_QUERY_KEY = ['admin', 'stripe', 'missing-customer'] as const;

export const stripeApi = {
  async listWebhooks(
    client: ApiClient,
    params: { status?: WebhookStatusFilter; source?: string; sinceHours?: number; limit?: number } = {},
  ): Promise<{ rows: WebhookRow[] }> {
    const sp = new URLSearchParams();
    if (params.status && params.status !== 'all') sp.set('status', params.status);
    if (params.source) sp.set('source', params.source);
    if (params.sinceHours !== undefined) sp.set('sinceHours', String(params.sinceHours));
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const tail = sp.toString();
    return client.get<{ rows: WebhookRow[] }>(
      `/stripe/webhooks${tail.length > 0 ? `?${tail}` : ''}`,
    );
  },
  async retryWebhook(
    client: ApiClient,
    source: string,
    eventId: string,
  ): Promise<{
    source: string;
    eventId: string;
    status: string;
    disposition: 'processed' | 'skipped';
    tenantId: string | null;
  }> {
    return client.post(`/stripe/webhooks/${source}/${encodeURIComponent(eventId)}/retry`, {});
  },
  async meterPusher(client: ApiClient): Promise<MeterPusherState> {
    return client.get<MeterPusherState>('/stripe/meter-pusher');
  },
  async flushMeterPusher(client: ApiClient): Promise<{ pushedCount: number }> {
    return client.post('/stripe/meter-pusher/flush', {});
  },
  async runReconciliation(
    client: ApiClient,
  ): Promise<{
    green: number;
    driftWarn: number;
    driftAlert: number;
    error: number;
    skippedNoMeter: number;
  }> {
    return client.post('/stripe/reconciliation/run', {});
  },
  async listDivergence(
    client: ApiClient,
    params: { onlyOpen?: boolean; limit?: number } = {},
  ): Promise<{ rows: DivergenceRow[] }> {
    const sp = new URLSearchParams();
    if (params.onlyOpen !== undefined) sp.set('onlyOpen', String(params.onlyOpen));
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const tail = sp.toString();
    return client.get<{ rows: DivergenceRow[] }>(
      `/stripe/divergence${tail.length > 0 ? `?${tail}` : ''}`,
    );
  },
  async resolveDivergence(client: ApiClient, id: string): Promise<DivergenceRow> {
    return client.post<DivergenceRow>(`/stripe/divergence/${id}/resolve`, {});
  },
  async listMissingCustomer(
    client: ApiClient,
    params: { limit?: number } = {},
  ): Promise<{ rows: MissingSubRow[] }> {
    const sp = new URLSearchParams();
    if (params.limit !== undefined) sp.set('limit', String(params.limit));
    const tail = sp.toString();
    return client.get<{ rows: MissingSubRow[] }>(
      `/stripe/missing-customer${tail.length > 0 ? `?${tail}` : ''}`,
    );
  },
};

export function webhookBadgeVariant(
  status: WebhookStatus,
  stuck: boolean,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (stuck) return 'destructive';
  switch (status) {
    case 'processed':
      return 'default';
    case 'received':
      return 'secondary';
    case 'rejected':
      return 'destructive';
    case 'skipped':
      return 'outline';
  }
}
